/**
 * TitleBar — DS-02.
 *
 * The dark strip at the top of every authenticated screen: the clinic's own name
 * on the reading edge, and on the far edge the three things a receptionist
 * glances at without stopping work — is the server still there, who am I logged
 * in as, and which language am I in.
 *
 * The name comes from §5.10 and nowhere else. Until an admin has filled it in
 * the bar simply carries no name: a hard-coded stand-in would be branding some
 * other clinic's install.
 *
 * It also owns `useOnlineStatus` because the status bar needs
 * exactly the same two answers and the chrome must never disagree with itself.
 */

import { forwardRef, useCallback, useSyncExternalStore } from 'react';
import type { HTMLAttributes } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Languages, Menu, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import type { CurrentUserDto, Language } from '@nova/shared';
import { useTranslation } from 'react-i18next';
import { LiveDot } from '@/components/ui';
import { useClinicName } from '@/features/settings/hooks';
import { api } from '@/lib/api';
import { applyLanguage, useLang } from '@/i18n';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
// One role-label table for the whole shell: the rail, the drawer and this bar
// all print the same principal, and two copies of the map drift apart.
import { sidebarRoleLabel } from './Sidebar';

function subscribeToConnection(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/**
 * DS-02 / DS-08 — the connection light behind both chrome bars. Subscribing to
 * the browser's own online/offline events rather than polling means the dot
 * flips the moment the wifi drops, and costs nothing while it does not.
 */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(
    subscribeToConnection,
    () => navigator.onLine,
    // Server snapshot: assume connected, so the bar never flashes "offline" on
    // the first paint of a perfectly healthy session.
    () => true,
  );
}

export interface TitleBarProps extends HTMLAttributes<HTMLElement> {
  version?: string;
  onlineLabel?: string;
  offlineLabel?: string;
  /** Accessible name and tooltip of the AR/EN toggle. */
  languageLabel?: string;
  /**
   * NFR-05 — opens the navigation drawer that stands in for the rail below
   * 1024px. Omit it and the bar renders no hamburger at all, which is what a
   * shell with a permanently visible sidebar wants.
   */
  onOpenNav?: () => void;
  navLabel?: string;
}

export const TitleBar = forwardRef<HTMLElement, TitleBarProps>(function TitleBar(
  {
    version = '1.0.0',
    onlineLabel,
    offlineLabel,
    languageLabel,
    onOpenNav,
    navLabel,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const online = useOnlineStatus();
  // Nobody signed in means nobody allowed to ask for the settings either.
  const clinicName = useClinicName(Boolean(user));
  // What the document is rendering in, not what the account was last saved as —
  // the two differ for the tick between the toggle and the PATCH that stores it.
  const lang: Language = useLang();
  const nextLang: Language = lang === 'AR' ? 'EN' : 'AR';

  // NFR-03 — the language belongs to the account, not to the browser, so a user
  // who switches machines keeps it. The switch itself is applied optimistically:
  // waiting for a round trip to flip the document direction feels broken.
  const { mutate: persistLanguage } = useMutation({
    mutationFn: (language: Language) =>
      api.patch<CurrentUserDto>('/users/me', { language }).then((r) => r.data),
    onSuccess: (updated) => setUser(updated),
    onError: () => toast.warning(t('language.deviceOnly')),
  });

  const toggleLanguage = useCallback(() => {
    applyLanguage(nextLang);
    const current = useAuthStore.getState().user;
    if (!current) return;
    // Keep the store in step immediately: the axios `Accept-Language` header and
    // every date formatter in the app read the language from here.
    setUser({ ...current, language: nextLang });
    persistLanguage(nextLang);
  }, [nextLang, persistLanguage, setUser]);

  const connectionLabel = online
    ? (onlineLabel ?? t('status.connected'))
    : (offlineLabel ?? t('status.disconnected'));
  const navButtonLabel = navLabel ?? t('a11y.openNav');
  const languageButtonLabel = languageLabel ?? t('language.change');
  const roleLabel = sidebarRoleLabel(user?.roleNames, t);

  return (
    <header
      ref={ref}
      // NFR-05 — 44px tall below 1024px so the hamburger is a thumb target, and
      // the same 36px strip of DS-02 at every desktop width.
      className={cn(
        'flex h-9 shrink-0 items-center gap-3 bg-slate-800 px-3 text-xs text-white max-lg:h-touch max-lg:gap-2 max-lg:px-1',
        className,
      )}
      {...rest}
    >
      {/* The rail is off screen below 1024px; this is what opens it. */}
      {onOpenNav ? (
        <button
          type="button"
          data-testid="mobile-nav-open"
          onClick={onOpenNav}
          title={navButtonLabel}
          aria-label={navButtonLabel}
          className="hidden h-touch w-touch shrink-0 items-center justify-center rounded text-slate-200
                     transition hover:bg-slate-700 hover:text-white max-lg:flex"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}

      <div className="flex min-w-0 items-center gap-2">
        {/* DS-45 — the glyph is redundant next to the hamburger on a narrow bar. */}
        <Stethoscope className="h-4 w-4 shrink-0 text-blue-400 max-lg:hidden" aria-hidden="true" />
        {clinicName ? <span className="truncate font-bold tracking-wide">{clinicName}</span> : null}
        {/* DS-51 — a version is a number, so it stays LTR and monospaced. */}
        <span className="ltr-num shrink-0 font-mono text-[10px] text-slate-500 max-lg:hidden">v{version}</span>
      </div>

      <div className="ms-auto flex shrink-0 items-center gap-3 max-lg:gap-1">
        {/* DS-02 / DS-42 — the dot pulses only while the connection is actually live.
            Its word is the first thing to go on a narrow bar: the colour already
            says it, and the drawer repeats the dot beside the user's name. */}
        <span className="flex items-center gap-1.5" title={connectionLabel}>
          <LiveDot tone={online ? 'emerald' : 'red'} active={online} />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300 max-lg:hidden">
            {connectionLabel}
          </span>
        </span>

        {/* Who is signed in is in the drawer on a phone, one tap away, rather
            than squeezed against the clinic name in a 44px strip. */}
        {user ? (
          <>
            <span className="h-4 w-px bg-slate-600 max-lg:hidden" aria-hidden="true" />
            <span className="flex min-w-0 items-center gap-1.5 max-lg:hidden">
              <span className="truncate font-bold" title={user.fullNameAr}>
                {user.fullNameAr}
              </span>
              {roleLabel ? (
                <span className="shrink-0 text-[10px] uppercase tracking-widest text-slate-400">
                  {roleLabel}
                </span>
              ) : null}
            </span>
          </>
        ) : null}

        <span className="h-4 w-px bg-slate-600 max-lg:hidden" aria-hidden="true" />

        <button
          type="button"
          onClick={toggleLanguage}
          data-testid="language-toggle"
          data-next-lang={nextLang}
          title={languageButtonLabel}
          aria-label={`${languageButtonLabel} — ${nextLang}`}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest
                     text-slate-300 transition hover:bg-slate-700 hover:text-white
                     max-lg:h-touch max-lg:w-touch max-lg:justify-center max-lg:px-0 max-lg:text-xs"
        >
          <Languages className="h-3.5 w-3.5 shrink-0 max-lg:hidden" aria-hidden="true" />
          {/* The button shows the language it switches to, not the one you are in. */}
          <span className="ltr-num font-mono">{nextLang}</span>
        </button>
      </div>
    </header>
  );
});

export default TitleBar;
